import React, { useState, useEffect, useContext, useRef } from "react";
import { get, find, hasIn, noop } from "lodash";
import { useDispatch } from "react-redux";
import { History } from "history";
import { useNavigate } from "react-router-dom";
import withUserPromos from "@tvg/utils/withUserPromos";
// @ts-ignore
import Context from "@fdr-mar/promos-context";
import { v4 as generateUUID } from "uuid";
import promoService from "@tvg/api/pro";
import mediator from "@tvg/mediator";
import tvgConf from "@tvg/conf";
import { TvgConf } from "@tvg/conf/src/types";
import {
  OptinTypeContext,
  Error as PromoError
} from "@fdr-mar/promos-types/Context";
// @ts-ignore
import CPPService, { CPP_SOURCE } from "@tvg/sh-cpp";
import { getSession } from "@urp/unified-modules/src/utils/account";
import { BinaryFn, NullaryFn } from "@tvg/ts-types/Functional";
import { isFDR } from "@tvg/utils/generalUtils";
import { getAndStoreCPPPromos } from "@tvg/sh-cpp/src/service/utils";
import { onbackPromosModal } from "../../redux/actions";
import Components from "../../_dictionary";
import BackButton from "../../components/BackButton";
import OptinButton from "../../components/OptinButton";
import { checkPromoStartDate } from "../../components/PromosBodyContent";
import {
  Blok,
  LinkType,
  PromosContent,
  User,
  Segment,
  ContentProps
} from "../../types";
import {
  LandingPageContainer,
  OptinFooterBtnWrapper
} from "./styled-components";

const { UserContext, OptinContext, PromoContext, PathContext } = Context;

type Platform = "ios" | "web" | "android" | "androidGPS";

export interface Props {
  content: ContentProps;
  user: User;
  promoId: number;
  promoType: Segment;
  promoCode: string;
  promoName?: string;
  error: PromoError;
  isOpted: boolean;
  platform: Platform;
  mobile: boolean;
  history: History;
  appVersion: string;
  cppService: CPPService;
  isDesktop?: boolean;
  getPromos?: BinaryFn<string, boolean, Promise<{ isOptedIn: boolean }>>;
  onOptIn?: NullaryFn<void>;
}

export const Page = ({
  content,
  user,
  promoId,
  promoType,
  promoCode,
  promoName,
  error,
  isOpted = false,
  platform,
  mobile,
  isDesktop,
  history,
  appVersion,
  cppService,
  getPromos = async () => ({ isOptedIn: false }),
  onOptIn = noop
}: Props) => {
  const [state, setState] = useState<OptinTypeContext>({
    isOpted: false,
    optedIn: false,
    error: "",
    handleOptinClick: () => {}
  });

  const pathContext = useContext(PathContext);
  const tvgConfInstance: TvgConf = tvgConf();
  const compRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  let isStaticCPPPromo = false;

  useEffect(() => {
    // GTM dataLayer PAGEVIEW push
    let promoSource = "Promos Page";
    let currentPlatform = platform;
    // @ts-ignore
    const { currentSlug } = pathContext;
    const loginStatus = get(user, "isLogged", false)
      ? "Logged In"
      : "Logged Out";
    const registrationStatus = get(user, "returningUser", false)
      ? "Registered"
      : "Unregistered";
    let siteVersion;

    if (tvgConfInstance.product === "fdrandroidgps") {
      currentPlatform = "androidGPS";
    } else if (tvgConfInstance.product.includes("fdrandroid")) {
      currentPlatform = "android";
    }

    if (
      mobile &&
      currentPlatform !== "ios" &&
      currentPlatform !== "android" &&
      currentPlatform !== "androidGPS"
    ) {
      siteVersion = "v2-revamp";
    } else if (mobile && currentPlatform === "ios") {
      siteVersion = "iOS";
    } else if (mobile && currentPlatform === "android") {
      siteVersion = "fdrandroid";
    } else if (mobile && currentPlatform === "androidGPS") {
      siteVersion = "fdr-android-gps";
    } else {
      siteVersion = "TVG4";
    }

    if (
      typeof history.location.state === "object" &&
      history.location.state !== null &&
      (history.location.state as { from?: string }).from === "quicklinks"
    ) {
      promoSource = "Home Page";
    }

    const LocalStorageSource = localStorage.getItem("PromoSource") || null;

    if (LocalStorageSource && LocalStorageSource === "Program Page") {
      promoSource = "Program Page";

      localStorage.removeItem("PromoSource");
    }

    mediator.base.dispatch({
      type: "PROMOS_PAGEVIEW",
      payload: {
        siteVersion,
        appVersion,
        accountId: get(user, "accountNumber", undefined),
        residenceState: get(user, "homeState", undefined),
        loginStatus,
        registrationStatus,
        page: currentSlug,
        promoSource
      }
    });

    // The following error and isOpted props permit to toggle state from Storybook
    setOptinError();
    setOptinState();

    if (mobile) scrollModalToTop();
  }, []);

  useEffect(() => {
    // The following error and isOpted props permit to toggle state from Storybook
    if (error !== null && error !== undefined) {
      setOptinError();
    }

    if (isOpted !== null && isOpted !== undefined) {
      setOptinState();
    }

    if (mobile) scrollModalToTop();
  }, [error, isOpted, content]);

  const setOptinError = () => {
    setState((prevState) => ({ ...prevState, error }));
  };

  const setOptinState = () => {
    setState((prevState) => ({ ...prevState, isOpted }));
  };

  const optinUser = async (
    accountNumber: string,
    id: number
  ): Promise<void> => {
    try {
      const isCPPPromo = get(content, "promo_type.source", "") === CPP_SOURCE;

      if (isCPPPromo) {
        await cppService
          .optIn(promoCode)
          .then((res: { success: string; errorCode: string }) => {
            const { success, errorCode } = res;

            if (!success) {
              throw new Error(errorCode);
            }
            trackOptIn(!success);

            onOptIn?.();
            getAndStoreCPPPromos(dispatch, true);
          })
          .catch(() => {
            mediator.base.dispatch({
              type: "PROMOS_OPT_IN_FAILED",
              payload: { promoId, promoName }
            });
          });
      } else {
        try {
          await promoService.postUserPromoOptin(accountNumber, id);
          trackOptIn(false);
          mediator.base.dispatch({
            type: "TVG_LOGIN:GET_USER_PROMOS"
          });
        } catch {
          trackOptIn(true);
        }
      }

      // TODO: IMPLEMENT LATER
      // if (typeof addUserToPromo === "function")
      //   addUserToPromo(id.toString(), promoCode);

      setState((prevState) => ({ ...prevState, isOpted: true, optedIn: true }));
      handleOptinDataLayerUpdate("Success");
    } catch (err) {
      const exception = get(err, "response.data.exception", "");
      // @ts-ignore
      setState((prevState) => ({
        ...prevState,
        error: exception || get(err, "message", "")
      }));
      handleOptinDataLayerUpdate("Failure");
    } finally {
      if (mobile) scrollModalToTop();
    }
  };

  const trackOptIn = (hasError: boolean) => {
    if (hasError) {
      mediator.base.dispatch({
        type: "PROMOS_OPT_IN_FAILED",
        payload: { promoId, promoName }
      });
    } else {
      mediator.base.dispatch({
        type: "PROMOS_OPT_IN_SUCCESSFUL",
        payload: { promoId, promoName }
      });
    }
  };

  const handleOptInSuccess = async (
    promoSource: string,
    promoPath: string,
    accountNumber: string
  ) => {
    const isCPP = promoSource === CPP_SOURCE;

    if (isCPP) {
      try {
        const promo = await getPromos(promoCode, true);
        history.push(promoPath);

        if (promo && !get(promo, "isOptedIn")) {
          optinUser(accountNumber, promoId);
        }
      } catch (er) {
        const exception = get(er, "response.data.exception");
        console.log(exception, "error");
      }
    } else {
      history.push(promoPath);
      optinUser(accountNumber, promoId);
    }
  };

  const handleTvg5Click = (link: string) => {
    if (link && (link.includes("registration") || link.includes("sign-up"))) {
      return navigate("/registration");
    }
    return mediator.base.dispatch({
      type: "OPEN_LOGIN",
      payload: {
        callback: (_: null, success: Object) => {
          if (get(success, "status", "fail") === "success") {
            const promoSource = get(content, "promo_type.source", "");
            const localAccountNumber = get(
              success,
              "data.userDetails.accountNumber",
              ""
            );
            handleOptInSuccess(
              promoSource,
              `/promos/${promoId}`,
              localAccountNumber
            );
          }
        }
      }
    });
  };

  const handleOptinClick = (link: string) => {
    const { isLogged, accountNumber } = user;

    if (!isLogged) {
      const sessionType =
        link && (link.includes("registration") || link.includes("sign-up"))
          ? "NewUser"
          : "Normal";

      if (isFDR()) {
        dispatch(onbackPromosModal(true));
        getSession(sessionType, ({ accountNumber: localAccountNumber }) => {
          const promoSource = get(content, "promo_type.source", "");
          handleOptInSuccess(
            promoSource,
            !isDesktop
              ? `${window.location.search}${window.location.hash}`
              : window.location.pathname,
            localAccountNumber
          );
        });
      } else {
        handleTvg5Click(link);
      }
    } else {
      optinUser(accountNumber, promoId);
    }
  };

  const handleOptinDataLayerUpdate = (tag: string): void => {
    mediator.base.dispatch({
      type: "PROMOS_RET_CTA_CLICK",
      payload: {
        gaEventAction: "Opt In",
        gaEventLabel: promoId,
        tag
      }
    });
  };

  const scrollModalToTop = (): void => {
    if (compRef.current) {
      compRef.current.scrollIntoView();
    }
  };

  const { isLogged, returningUser, optedInPromos } = user;
  const showBackBtn = get(history, "location.state.showBackBtn", false);
  const hasContent = find(content.body, ["component", "content_section"]) as
    | PromosContent
    | undefined;
  let contentBtnProps: PromosContent = {
    forOptin: false,
    linkLabel: "",
    path: {
      id: "",
      url: "",
      cached_url: "",
      linktype: "url" as LinkType
    },
    isForBanner: false
  };

  const isCPPPromo = get(content, "promo_type.source") === CPP_SOURCE;

  if (hasContent && hasIn(hasContent, "bloks")) {
    // @ts-ignore
    contentBtnProps = find(hasContent.bloks, ["component", "optin_button"]);

    const handicappingExists = hasContent?.bloks?.some(
      (blok: Blok) => blok.component === "handicapping"
    );

    if (!handicappingExists) {
      // @ts-ignore
      hasContent?.bloks?.push({
        component: "handicapping",
        _uid: generateUUID()
      });
    }
  }

  // @ts-ignore
  const date = checkPromoStartDate(hasContent ? hasContent.bloks : []);

  return (
    <PromoContext.Provider value={{ promoType, promoCode, date }}>
      <UserContext.Provider value={{ isLogged, returningUser, optedInPromos }}>
        <OptinContext.Provider value={{ ...state, handleOptinClick }}>
          <LandingPageContainer
            data-qa-label="landingPageContainer"
            ref={compRef}
            isCPPPromo={isCPPPromo}
          >
            {showBackBtn && mobile && <BackButton history={history} />}
            {content?.body?.map((blok) => {
              isStaticCPPPromo =
                isCPPPromo &&
                !find(blok?.bloks, {
                  component: "info_section"
                });
              return Components(blok);
            })}
            {mobile && contentBtnProps && (
              <OptinFooterBtnWrapper data-qa-label="optinFooterBtnWrapper">
                <OptinButton
                  content={contentBtnProps}
                  isStaticPromo={isStaticCPPPromo}
                />
              </OptinFooterBtnWrapper>
            )}
          </LandingPageContainer>
        </OptinContext.Provider>
      </UserContext.Provider>
    </PromoContext.Provider>
  );
};

export default withUserPromos(Page);
